import { useCallback, useEffect, useState } from 'react';
import { SxmOutreach, OutreachChannel, OutreachResult } from '@/features/sales/types';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { useAutomationUserContext } from './useAutomationUserContext';

export interface CreateSxmOutreachDto {
  leadId?: number;
  agentId?: string;
  channel?: OutreachChannel;
  message?: string;
  result?: OutreachResult;
}

export interface UpdateSxmOutreachDto {
  leadId?: number;
  agentId?: string;
  channel?: OutreachChannel;
  message?: string;
  result?: OutreachResult;
}

export const useSxmOutreach = (enabled: boolean = false, page: number = 1, limit: number = 10) => {
  const { user, targetUserId, isInternalUser } = useAutomationUserContext();
  const [outreach, setOutreach] = useState<SxmOutreach[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOutreach = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const endpoint = isInternalUser && targetUserId !== user?.id ? '/sxm/internal/outreach' : '/sxm/outreach';
      const { data } = await api.get(endpoint, {
        params: {
          userId: targetUserId,
          page,
          limit
        }
      });

      if (data && Array.isArray(data.data)) {
        setOutreach(data.data);
        setTotal(data.total);
      } else if (Array.isArray(data)) {
        setOutreach(data);
        setTotal(data.length);
      } else {
        setOutreach([]);
        setTotal(0);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [targetUserId, enabled, isInternalUser, user?.id, page, limit]);

  const createOutreach = async (dto: CreateSxmOutreachDto): Promise<SxmOutreach> => {
    try {
      const { data: newOutreach } = await api.post('/sxm/outreach', dto, { params: { userId: targetUserId } });
      if (enabled) {
        await fetchOutreach();
      } else {
        setOutreach(prev => [...prev, newOutreach]);
      }
      return newOutreach;
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  const updateOutreach = async (id: number, dto: UpdateSxmOutreachDto): Promise<SxmOutreach> => {
    try {
      const { data: updatedOutreach } = await api.patch(`/sxm/outreach/${id}`, dto, { params: { userId: targetUserId } });
      setOutreach(prev => prev.map(item => item.id === id ? updatedOutreach : item));
      return updatedOutreach;
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  const bulkUpdateOutreach = async (updates: Array<{ id: number; data: UpdateSxmOutreachDto }>): Promise<SxmOutreach[]> => {
    try {
      const { data: updatedRecords } = await api.patch('/sxm/outreach/bulk', updates, { params: { userId: targetUserId } });
      if (enabled) {
        await fetchOutreach();
      } else {
        setOutreach((prev): SxmOutreach[] => {
          const updatedMap = new Map<number, SxmOutreach>(updatedRecords.map((item: SxmOutreach) => [item.id, item]));
          return prev.map(item => updatedMap.get(item.id) ?? item);
        });
      }
      return updatedRecords;
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  const deleteOutreach = async (id: number): Promise<void> => {
    try {
      await api.delete(`/sxm/outreach/${id}`, { params: { userId: targetUserId } });
      setOutreach(prev => prev.filter(item => item.id !== id));
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchOutreach();
    }
  }, [enabled, fetchOutreach]);

  return {
    outreach,
    total,
    loading,
    error,
    createOutreach,
    updateOutreach,
    bulkUpdateOutreach,
    deleteOutreach,
    refetch: fetchOutreach,
  };
};
